import * as React from 'react'
import * as Redux from 'react-redux'
import { useLocation } from 'react-router-dom'
import * as jotai from 'jotai'
import { styled, css } from 'next-yak'

import * as plume from '@ulule/owl-kit-components/next'
import * as layout from '@ulule/layout'

import {
  BREAKPOINT_PROJECT_EDIT_MODE,
  type BreakpointType,
  ConfiguredNavMenu,
  PM_MENU_WIDTH,
  Screen,
} from '@owl-nest/project-manager-nav'
import * as cookie from '@owl-nest/cookie-helper'
import * as env from '@owl-nest/config'
import * as hooks from '@owl-nest/hooks'
import * as messages from '@owl-nest/messages'
import * as models from '@owl-nest/models'
import * as services from '@owl-nest/services'
import * as shadow from '@owl-nest/shadow'
import { Footer, getNavSectionList } from '@owl-nest/layout'
import { StickyStatus, stickyContext, useStickyFromContext } from '@owl-nest/sticky-within'
import { renderConfetti } from '@owl-nest/confetti'
import { scroll, lastAction } from '@owl-nest/utils'
import { t } from '@owl-nest/localize'

import * as UFE from './../../UFE'
import * as modalAction from '../../actions/modal'
import * as projectAction from '../../actions/project'
import * as projectModel from '../models/project'
import { type RootState } from '../../reducers'
import { useDisplayMode } from '../hooks/useDisplayMode'
import { useNavigation } from '../hooks/useNavigation'

import { BackersBlock, BlockStatus } from '../views/detail/components/BackersBlock'
import { CATEGORY } from '@owl-nest/shadow'
import { DisplayMode } from '../context'
import { EulaModal } from '@owl-nest/eula-modal'
import { HeaderLayout } from './HeaderLayout'
import { HeroHeader } from '../views/detail/components/HeroHeader'
import { Menu } from '../views/detail/components/Menu'
import { Onboarding } from '../components/Onboarding'
import { PATHS } from '../paths'
import { PublicLayout } from './PublicLayout'
import { SuggestedProjectsList } from '../views/detail/components/SuggestedProjectsList'
import { TRACKING_ACTION, TRACKING_SOURCE } from '../views/detail/utils/trackerEnums'
import { TabRouter } from '../TabRouter'
import { useReduxProjectFollowing } from '../views/detail/hooks/useReduxProjectFollowing'

import { EDIT_MODE_TABLET_BREAKPOINT } from '../views/detail/constants'

const STICKY_TARGET_MENU_SELECTOR = 'sticky-menu-' + Math.random().toString(36).slice(2)
const StickyContextBase = stickyContext(STICKY_TARGET_MENU_SELECTOR, {
  sticky: (status) => {
    switch (status) {
      case StickyStatus.PreSticky: {
        return `
          position: absolute;
          top: 0;
        `
      }
      case StickyStatus.Sticky:
      case StickyStatus.PostSticky: {
        return `
          position: fixed;
          top: 0;
        `
      }
    }
  },
  fallbackToSticky: true,
})

const STICKY_TARGET_HEADER_SELECTOR = 'sticky-header-' + Math.random().toString(36).slice(2)
const StickyHeaderContext = stickyContext(STICKY_TARGET_HEADER_SELECTOR, {
  sticky: () => {
    return ``
  },
  fallbackToSticky: true,
})

export const SCROLL_TO_ATOM = jotai.atom('')

export function DetailLayout(): React.ReactElement<unknown> {
  const dispatch = Redux.useDispatch()

  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const userConfig = env.useUserEnv()

  const role = models.project.getEditingUserRole(project, UFE.CURRENT_USER.is_staff)

  const showOnboarding = models.project.showOnboardingSteps(project)
  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'

  const contextRef = React.useRef<HTMLDivElement>(null)
  const stickyStatus = useStickyFromContext(contextRef, { top: -60 })
  const isSticky = stickyStatus !== StickyStatus.PreSticky

  const headerContextRef = React.useRef<HTMLDivElement>(null)
  const headerStickyStatus = useStickyFromContext(headerContextRef, { top: 10 })
  const isHeaderSticky = headerStickyStatus === StickyStatus.Sticky

  const setScrollTo = jotai.useSetAtom(SCROLL_TO_ATOM)
  const setIsProjectDetailLaunchShareModalOpen = hooks.useSetModal('ProjectDetailLaunchShare')

  const navigation = useNavigation()
  const location = useLocation()

  const { href, search } = window.location
  const urlParams = new URLSearchParams(search)
  const hasConfetti = urlParams.has('confettis')
  const messenger = messages.useMessage()

  const breakpointType: BreakpointType = isEditMode ? 'custom-project-edit-mode' : 'tablet'
  const breakpoint =
    breakpointType === 'custom-project-edit-mode' ? BREAKPOINT_PROJECT_EDIT_MODE : plume.BREAKPOINTS.TABLET

  const isTablet = hooks.useDramaticallyInefficientMediaQuery(breakpoint)

  const firstLocationChangeRef = React.useRef(true)

  const auth = services.user.useAuth()

  const { toggleFollowing } = useReduxProjectFollowing({
    trackingExtraData: {
      eventCategory: CATEGORY.PROJECT_PAGE,
      eventAction: TRACKING_ACTION.FOLLOW_PROJECT,
      eventLabel: TRACKING_SOURCE.PROJECT,
    },
  })

  React.useLayoutEffect(() => {
    const locationState = location?.state as any

    if (navigation.is(PATHS.PROJECT) && locationState?.scrollTarget !== undefined) {
      setScrollTo(locationState?.scrollTarget)
    } else {
      // When changing tabs, scroll the top of the new active tab into view.
      // To avoid interfering with in-page anchors, we check for hash existence first.
      if (
        (!firstLocationChangeRef.current && !location.hash) ||
        ['#comments/', '#faq/', '#rewards/'].includes(location.hash)
      ) {
        // HACK: `setTimeout` is necessary for Gecko (Firefox), which otherwise may skip the scrolling instruction.
        setTimeout(function () {
          scroll.animatedScrollTo(getOffsetTop(contextRef.current), 100)
        }, 5)
      } else {
        setScrollTo('')
      }
    }

    firstLocationChangeRef.current = false
  }, [location])

  React.useEffect(() => {
    services.project.like.applyLastActionLike().then((response) => {
      if (!response) {
        return
      }

      response.caseOf({
        left: () => {
          messenger.error({
            context: 'error',
            message: t('Oops... An error occurred while following the project. Please try again.'),
            type: 'toast',
          })
        },
        right: () => {
          dispatch(modalAction.toggleModalFollow(true))
          toggleFollowing()
        },
      })
    })
  }, [])

  React.useEffect(() => {
    const toast = new URLSearchParams(window.location.search).get('toast')

    if (toast) {
      messenger.confirm({
        context: 'from-url-param',
        forceClose: true,
        message: toast,
        type: 'toast',
      })
    }

    const error = new URLSearchParams(window.location.search).get('error_toast')

    if (error) {
      messenger.error({
        context: 'from-url-param',
        forceClose: true,
        message: error,
        type: 'toast',
      })
    }

    const rewardId = new URLSearchParams(window.location.search).get('reward')
    if (rewardId) {
      dispatch(modalAction.setRewardId(rewardId))
      dispatch(modalAction.toggleModalReward(true))
    }

    const launch = new URLSearchParams(window.location.search).get('launch')

    if (launch) {
      setIsProjectDetailLaunchShareModalOpen(true)
    }

    urlParams.delete('error_toast')
    urlParams.delete('toast')
    urlParams.delete('reward')
    const newUrl = href.replace(search.substring(1), urlParams.toString())
    window.history.replaceState({}, '', newUrl)
  }, [])

  React.useEffect(() => {
    // check url parameters
    if (!hasConfetti) {
      return
    } else {
      urlParams.delete('confettis')
      // replace url parameters
      const newUrl = href.replace(search.substring(1), urlParams.toString())
      window.history.replaceState({}, '', newUrl)
      // confetti
      renderConfetti()
    }
  }, [])

  // TODO: IMPROVEMENT: This should be a global behaviour (as in the good old `features/init`)
  const { sendBrandsOnPage } = shadow.usePartnershipTracker(undefined, { current: null }, {})
  React.useEffect(() => {
    sendBrandsOnPage()
  }, [])

  if (models.project.isCancelled<projectModel.ExtraFields>(project)) {
    return (
      <DisplayMode.Provider value={displayMode}>
        <CancelledWrapper $isEditMode={isEditMode}>
          <HeaderLayout isHeaderSticky={isHeaderSticky} className={STICKY_TARGET_HEADER_SELECTOR} />
          <BackersBlock status={BlockStatus.CANCELLED} />
        </CancelledWrapper>

        {isTablet && isEditMode && (
          <TabletNavMenuContainer>
            <ConfiguredNavMenu
              mobileBreakpoint={isEditMode ? 'custom-project-edit-mode' : undefined}
              project={project}
              role={role}
              screen={Screen.MY_PROJECT}
            />
          </TabletNavMenuContainer>
        )}
        <LayoutWrapper $isEditMode={isEditMode}>
          <AppWrapper $isEditMode={isEditMode}>
            <PublicLayout>
              <SuggestedProjectsList isEditMode={isEditMode} />
            </PublicLayout>
          </AppWrapper>
        </LayoutWrapper>
        <Footer lang={userConfig.lang} navSections={getNavSectionList(userConfig.lang)} />
      </DisplayMode.Provider>
    )
  }

  return (
    <DisplayMode.Provider value={displayMode}>
      {auth.type === 'loggedin' && <EulaModal user={auth.user} refresh={auth.refresh} />}
      <StyledToastDisplayer contexts={['from-url-param', 'delete-comment']} position="top" />
      {project.nsfw && cookie.getCookie(`accept_pegi_18_${project.id}`) !== 'true' && (
        <layout.modals.Nsfw
          onBack={() => {
            window.location.href = '/'
          }}
          projectId={project.id}
          projectName={models.project.name(project) ?? ''}
          projectSlug={project.slug}
        />
      )}
      <StickyHeaderContext $status={headerStickyStatus}>
        <div ref={headerContextRef} />
        <HeaderLayout isHeaderSticky={isHeaderSticky} className={STICKY_TARGET_HEADER_SELECTOR} />
        <HeroHeader />
      </StickyHeaderContext>

      {isTablet && isEditMode && (
        <TabletNavMenuContainer>
          <ConfiguredNavMenu
            project={project}
            role={role}
            screen={Screen.MY_PROJECT}
            mobileBreakpoint={breakpointType}
          />
        </TabletNavMenuContainer>
      )}

      <LayoutWrapper $isEditMode={isEditMode}>
        {/* This ref is needed to make a scroll to the tabPanel on path change */}
        <div ref={contextRef} />
        <AppWrapper $isEditMode={isEditMode}>
          <PublicLayout>
            <StickyContext $status={stickyStatus}>
              <Menu sticky={isSticky} className={STICKY_TARGET_MENU_SELECTOR} />
              <TabRouter />
            </StickyContext>
          </PublicLayout>
        </AppWrapper>
      </LayoutWrapper>
      {/* @ts-expect-error ExtraFields mismatch */}
      {showOnboarding && <Onboarding project={project} />}
      <Footer lang={userConfig.lang} navSections={getNavSectionList(userConfig.lang)} />
    </DisplayMode.Provider>
  )
}

const StyledToastDisplayer = styled(messages.ToastDisplayer)`
  z-index: ${plume.ZINDEX.MODAL};
  top: ${plume.SIZES.HEADER_DESKTOP_HEIGHT};
`

const TabletNavMenuContainer = styled.div`
  position: absolute;
  top: 60px;
  width: ${PM_MENU_WIDTH};
`

const CancelledWrapper = styled.div<{ $isEditMode: boolean }>`
  ${({ $isEditMode }) =>
    $isEditMode &&
    css`
      @media screen and ${EDIT_MODE_TABLET_BREAKPOINT} {
        border-left: 1px solid ${plume.COLORS.PRIMARY_SAND_100};
        margin-left: ${PM_MENU_WIDTH};
      }
    `};
`

const AppWrapper = styled.main<{ $isEditMode: boolean }>`
  width: 100%;

  ${({ $isEditMode }) =>
    $isEditMode &&
    css`
      @media screen and ${EDIT_MODE_TABLET_BREAKPOINT} {
        border-left: 1px solid ${plume.COLORS.PRIMARY_SAND_100};
      }
    `}
`

const LayoutWrapper = styled.div<{ $isEditMode: boolean }>`
  display: flex;
  position: relative;
  z-index: 2;

  ${({ $isEditMode }) =>
    $isEditMode &&
    css`
      @media screen and ${EDIT_MODE_TABLET_BREAKPOINT} {
        left: ${PM_MENU_WIDTH};
        width: calc(100% - ${PM_MENU_WIDTH});
      }
    `}
`

const StickyContext = styled(StickyContextBase)`
  z-index: 0;

  ${Menu} {
    z-index: ${plume.ZINDEX.NAVIGATION_MENU};
    left: 0;
    right: 0;
  }
`

function getOffsetTop(element: any): number {
  return element ? element.offsetTop + getOffsetTop(element.offsetParent) : 0
}
